/**
 * Nested list section - collapsible group of todos under a parent todolist.
 * Continuity gestures: select (+ preview) / double-click open / right-click menu.
 */

import { useState } from 'react'
import { Check, ChevronDown, Square } from 'lucide-react'
import { cn } from '@/lib/utils'
import { isModClick } from '@/lib/open-intent'
import { ExtensionListRow } from '@/components/integrations/_shared'
import { SlideReveal } from '@/components/ui/slide-reveal'
import { BasecampDueBadge } from './basecamp-due-badge'
import type { BasecampTodoList } from '@/types/basecamp'

interface NestedListSectionProps {
  section: BasecampTodoList
  projectId: string
  projectName: string
  selectedTodoId?: string | null
  onSelectTodo: (todoId: string, todoTitle: string, e?: React.MouseEvent) => void
  onOpenTodo: (todoId: string, todoTitle: string, e?: React.MouseEvent) => void
  onTodoContextMenu: (
    todoId: string,
    todoTitle: string,
    e: React.MouseEvent,
  ) => void
  onSectionClick?: (sectionId: string, sectionTitle: string) => void
  onSectionContextMenu?: (
    sectionId: string,
    sectionTitle: string,
    e: React.MouseEvent,
  ) => void
  defaultExpanded?: boolean
}

export function NestedListSection({
  section,
  selectedTodoId,
  onSelectTodo,
  onOpenTodo,
  onTodoContextMenu,
  onSectionClick,
  onSectionContextMenu,
  defaultExpanded = false,
}: NestedListSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const activeTodos = section.todos.filter((t) => !t.completed)
  const completedTodos = section.todos.filter((t) => t.completed)
  const countLabel =
    section.todos.length === 0
      ? 'Empty'
      : `${activeTodos.length} open · ${completedTodos.length} done`

  const renderTodoRow = (
    todo: (typeof activeTodos)[number],
    opts?: { completed?: boolean },
  ) => {
    const id = String(todo.id)
    return (
      <li key={`section-${section.id}-todo-${todo.id}${opts?.completed ? '-done' : ''}`}>
        <ExtensionListRow
          selected={selectedTodoId === id}
          onSelect={(e) => {
            if (isModClick(e)) {
              onOpenTodo(id, todo.title, e)
              return
            }
            onSelectTodo(id, todo.title, e)
          }}
          onOpen={(e) => onOpenTodo(id, todo.title, e)}
          onContextMenu={(e) => onTodoContextMenu(id, todo.title, e)}
          className={cn('items-center pl-8', opts?.completed && 'opacity-60')}
        >
          {opts?.completed ? (
            <Check className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <Square className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div
              className={cn(
                'min-w-0 flex-1 truncate text-sm',
                opts?.completed && 'line-through',
              )}
            >
              {todo.title}
            </div>
            {todo.due_on && !opts?.completed ? (
              <BasecampDueBadge dueOn={todo.due_on} className="shrink-0" />
            ) : null}
          </div>
        </ExtensionListRow>
      </li>
    )
  }

  return (
    <div className="border-b border-border/50 last:border-b-0">
      <ExtensionListRow
        selected={false}
        onSelect={() => setIsExpanded((v) => !v)}
        onOpen={() => onSectionClick?.(section.id, section.title)}
        onContextMenu={(e) =>
          onSectionContextMenu?.(section.id, section.title, e)
        }
        trailing={
          <span className="text-[10px] tabular-nums text-muted-foreground">
            {countLabel}
          </span>
        }
        className="items-center"
      >
        <span className="flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground">
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 motion-safe:transition-transform',
              isExpanded ? 'rotate-0' : '-rotate-90',
            )}
          />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{section.title}</div>
        </div>
      </ExtensionListRow>

      <SlideReveal open={isExpanded}>
        {activeTodos.length === 0 && completedTodos.length === 0 ? (
          <p className="px-6 py-2 text-xs text-muted-foreground">
            No active todos in this section
          </p>
        ) : (
          <ul className="pb-1">
            {activeTodos.map((todo) => renderTodoRow(todo))}
            {completedTodos.map((todo) =>
              renderTodoRow(todo, { completed: true }),
            )}
          </ul>
        )}
      </SlideReveal>
    </div>
  )
}
